/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useEffect } from "react";
import dayjs from "dayjs";
import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then, When } from "react-if";
import { Box, FlexContainer, Spacer, theme } from "@nordcloud/gnui";
import { EventsSortFields, SortOrder } from "~/generated/graphql";
import { NoData } from "~/components";
import { BreadcrumbsBox } from "~/components/BreadcrumbsBox";
import {
  minSearchLength,
  TableSearchInput,
  getNoSearchResultsMessage,
} from "~/components/SearchInput";
import { SortSelector } from "~/components/SortSelector";
import { dateFormat } from "~/constants";
import { useQueryState } from "~/hooks";
import type { HistoryUpdateMode } from "~/hooks/useQueryState/useQueryState";
import { ROUTES } from "~/routing/routes";
import {
  QueryGetEvents,
  SortFields,
  sortOptions,
  weekBegin,
  weekEnd,
} from "./components/constants";
import { EventsFilters } from "./components/EventFilters";
import { EventFiltersSidebar } from "./components/EventFiltersSidebar";
import { EventsTable } from "./components/EventsTable";
import { IncomingEventsTable } from "./components/IncomingEventsTable";
import { EventType, Period } from "./types";

const breadcrumbsLabels = [
  { label: "Dashboard", uri: "/" },
  { label: "Events", uri: ROUTES.events.index },
];

export function EventsPage() {
  const { queryState, updateQueryState } = useQueryState<QueryGetEvents>();

  const handleSortDirection = useCallback(
    (order: SortOrder, mode: HistoryUpdateMode = "push") =>
      updateQueryState({ ...queryState, order }, mode),
    [queryState, updateQueryState]
  );

  const handleSortField = useCallback(
    (field: SortFields, mode: HistoryUpdateMode = "push") =>
      updateQueryState({ ...queryState, sortField: field }, mode),
    [queryState, updateQueryState]
  );

  const handlePhraseField = useCallback(
    (field: string | undefined, mode: HistoryUpdateMode = "push") =>
      updateQueryState({ ...queryState, searchPhrase: field }, mode),
    [queryState, updateQueryState]
  );

  const handleDateChange = useCallback(
    (
      startDate: string,
      endDate: string,
      viewPeriod: Period,
      mode: HistoryUpdateMode = "push"
      // eslint-disable-next-line max-params
    ) => {
      const startDateUTC = dayjs(startDate)
        .tz("UTC")
        .format(dateFormat.fullDate);

      const endDateUTC = dayjs(endDate).tz("UTC").format(dateFormat.fullDate);

      updateQueryState(
        {
          ...queryState,
          page: "1",
          startDateUTC,
          endDateUTC,
          viewPeriod: viewPeriod,
        },
        mode
      );
    },
    [queryState, updateQueryState]
  );

  const handleEventTypeChange = useCallback(
    (type: EventType, mode: HistoryUpdateMode = "push") => {
      updateQueryState(
        {
          ...queryState,
          eventsType: type,
          order: undefined,
          eventStatuses: [],
          excludeEventStatuses: false,
          eventLogOutputs: [],
          excludeEventLogOutputs: false,
          searchPhrase: undefined,
          page: "1",
        },
        mode
      );
    },
    [queryState, updateQueryState]
  );

  const isEmptyDateRange = !queryState.startDateUTC && !queryState.endDateUTC;

  useEffect(() => {
    if (isEmptyDateRange) {
      handleDateChange(weekBegin, weekEnd, Period.week, "replace");
    }
    if (!queryState.eventsType) {
      handleEventTypeChange(EventType.pastCurrent, "replace");
    }
    if (!queryState.order) {
      if (queryState.eventsType === EventType.future) {
        handleSortDirection(SortOrder.Asc, "replace");
      } else {
        handleSortDirection(SortOrder.Desc, "replace");
      }
    }
    if (!queryState.sortField) {
      handleSortField(EventsSortFields.StartTime, "replace");
    }
  }, [
    handleDateChange,
    handleEventTypeChange,
    handleSortDirection,
    handleSortField,
    isEmptyDateRange,
    queryState.eventsType,
    queryState.order,
    queryState.sortField,
  ]);

  const showCurrentEvents =
    !queryState.eventsType || queryState.eventsType === EventType.pastCurrent;

  const isSearchAllowed =
    !queryState.searchPhrase ||
    queryState.searchPhrase.length >= minSearchLength;

  return (
    <>
      <Row>
        <Col>
          <BreadcrumbsBox
            title="Events"
            customBreadcrumbs={breadcrumbsLabels}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Box>
            <EventsFilters
              handleDateChange={handleDateChange}
              handleEventTypeChange={handleEventTypeChange}
            />
            <Box boxStyle="lightGrey" spacing="spacing04">
              <FlexContainer justifyContent="space-between">
                <SortSelector<SortFields>
                  options={sortOptions}
                  value={queryState.sortField ?? EventsSortFields.StartTime}
                  direction={queryState.order ?? SortOrder.Desc}
                  onSelect={handleSortField}
                  onSortChange={handleSortDirection}
                />
                <Spacer width={theme.spacing.spacing04} />
                <TableSearchInput<QueryGetEvents>
                  onSubmit={handlePhraseField}
                  placeholder={`Type a Keyword (e.g. Plan Name, Plan ID, etc.)`}
                />
                <Spacer width={theme.spacing.spacing04} />
                <EventFiltersSidebar />
              </FlexContainer>
            </Box>
            <When condition={!isEmptyDateRange}>
              <If condition={!isSearchAllowed}>
                <Then>
                  <NoData hasIcon message={getNoSearchResultsMessage()} />
                </Then>
                <Else>
                  <If condition={showCurrentEvents}>
                    <Then>
                      <EventsTable />
                    </Then>
                    <Else>
                      <IncomingEventsTable />
                    </Else>
                  </If>
                </Else>
              </If>
            </When>
          </Box>
        </Col>
      </Row>
    </>
  );
}
